import { Box, Stack, Tooltip } from '@mui/material'
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { EmotionCache } from '@emotion/cache'
import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import { createPortal } from 'react-dom'
import YAML from 'yaml'
import {
  AssetTypeDefinition,
  findAssetTypeByLabel,
  type AssetReferenceCollector,
  type AssetHandle,
  type RenderData,
  type AssetRenderComponentProps,
} from './assetTypes'
import { collectAssetReferencesFromRenderData, collectAssetReferencesRecursive } from './shared/assetReferences'
import { assetParamsBus } from './assetParamsBus'

type AssetRendererProps = {
  content: string
  assetType?: AssetTypeDefinition
  renderData?: RenderData
  previewWidth?: number
  previewHeight?: number
  reloadToken?: number
  instanceId?: string
  layout?: 'fixed' | 'fill'
  showLog?: boolean
  forwardLogs?: boolean
  previewSx?: SxProps<Theme>
}

type AssetStatus = 'loading' | 'ready' | 'error'
type RenderLogLine = {
  text: string
  collapsedLines?: string[]
}

type RawEntity = Record<string, unknown>
type TrellisUiPalette = {
  primary: string
  track: string
  text: string
  error: string
}

type ResolvedPreloadedAsset = {
  handle: AssetHandle
  content: string
  renderData: RenderData
  collectAssetReferences?: AssetReferenceCollector
}

const TEXT_MIME_TYPES = new Set(['application/json', 'image/svg+xml'])
const SQL_CONTINUATION_LINE_PATTERN = /^\[[^\]]+\]\s>\s?(.*)$/
const RAW_SQL_CONTINUATION_LINE_PATTERN = /^>\s?(.*)$/

export const RENDER_WIDTH = 560
export const RENDER_HEIGHT = (9 / 16) * RENDER_WIDTH

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const sanitizeScript = (value: string) =>
  value
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')

const createInstanceId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `asset-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`
}

const normalizeAssetId = (value: string) => value.trim().toLowerCase()

const buildStatusHtml = (title: string, detail?: string) => {
  const safeTitle = escapeHtml(title)
  const safeDetail = detail ? escapeHtml(detail) : ''
  const message = detail ? `${title} ${detail}` : title
  const serializedMessage = sanitizeScript(JSON.stringify(message))
  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body {
        margin: 0;
        font-family: 'JetBrains Mono', Consolas, monospace;
        background-color: #ffffff;
        color: #111827;
        padding: 16px;
      }
      .title {
        font-weight: 600;
        margin-bottom: 8px;
      }
      .detail {
        color: #b91c1c;
        white-space: pre-wrap;
      }
    </style>
  </head>
  <body>
    <div class="title">${safeTitle}</div>
    ${safeDetail ? `<div class="detail">${safeDetail}</div>` : ''}
    <script>
      const message = ${serializedMessage}
      const trellis = window.trellis
      if (trellis && typeof trellis.log === 'function') {
        trellis.log(message)
      } else {
        window.parent?.postMessage(
          {
            source: 'asset-renderer',
            type: 'log',
            timestamp: new Date().toLocaleTimeString(),
            message,
          },
          '*'
        )
      }
    </script>
  </body>
</html>`
}

const buildMissingBuildHtml = (assetType?: AssetTypeDefinition) => {
  const typeLabel = assetType?.id ? `"${assetType.id}"` : '(unknown asset type)'
  return buildStatusHtml(
    'Asset runtime contract violation.',
    `Missing required runtime function: buildHtml(content, options) for ${typeLabel}.`
  )
}

const buildLoadingHtml = (message: string) => {
  const serializedMessage = sanitizeScript(JSON.stringify(message))
  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html,
      body {
        height: 100%;
      }
      body {
        margin: 0;
        background-color: #ffffff;
      }
    </style>
  </head>
  <body>
    <script>
      const message = ${serializedMessage}
      const trellis = window.trellis
      if (trellis && trellis.ui && typeof trellis.ui.showLoading === 'function') {
        trellis.ui.showLoading('')
      }
      if (trellis && typeof trellis.log === 'function') {
        trellis.log(message)
      } else {
        window.parent?.postMessage(
          {
            source: 'asset-renderer',
            type: 'log',
            timestamp: new Date().toLocaleTimeString(),
            message,
          },
          '*'
        )
      }
    </script>
  </body>
</html>`
}

const buildTrellisRuntimeScript = (assets: AssetHandle[], instanceId: string, uiPalette: TrellisUiPalette) => {
  const serializedAssets = sanitizeScript(JSON.stringify(assets))
  const serializedInstanceId = sanitizeScript(JSON.stringify(instanceId))
  const serializedUiPalette = sanitizeScript(JSON.stringify(uiPalette))
  return `
    <script>
      (function () {
        const instanceId = ${serializedInstanceId}
        const uiPalette = ${serializedUiPalette}
        const resolveColor = (value, fallback) =>
          typeof value === 'string' && value.trim().length > 0 ? value.trim() : fallback
        const uiPrimaryColor = resolveColor(uiPalette.primary, '#5c3cd8')
        const uiTrackColor = resolveColor(uiPalette.track, 'rgba(92, 60, 216, 0.24)')
        const uiTextColor = resolveColor(uiPalette.text, '#64748b')
        const uiErrorColor = resolveColor(uiPalette.error, '#d32f2f')
        const paramsState = { values: {}, revision: 0 }
        let hasReceivedHostUpdate = false
        const runtimeFunctions = new Map()
        const runtimeParamsListenerName = 'onParamsChanged'
        const normalizeName = (value) => String(value || '').trim()
        const normalizeParamsList = (params) =>
          Array.isArray(params)
            ? params.map((param) => String(param || '').trim()).filter((param) => param.length > 0)
            : []
        const toRuntimeRegistrationInfo = (name, entry) => ({
          name,
          params: [...entry.params],
          description: entry.description,
          source: 'runtime',
        })
        const cloneParamValue = (value) => (Array.isArray(value) ? [...value] : value)
        const cloneParams = () => {
          const cloned = {}
          const source = paramsState.values && typeof paramsState.values === 'object' ? paramsState.values : {}
          Object.keys(source).forEach((key) => {
            cloned[key] = cloneParamValue(source[key])
          })
          return cloned
        }
        const isParamValueEqual = (left, right) => {
          if (Array.isArray(left) && Array.isArray(right)) {
            if (left.length !== right.length) return false
            for (let index = 0; index < left.length; index += 1) {
              if (left[index] !== right[index]) return false
            }
            return true
          }
          if (Array.isArray(left) || Array.isArray(right)) return false
          return left === right
        }
        const getChangedKeys = (previousParams, nextParams) => {
          const allKeys = new Set([...Object.keys(previousParams), ...Object.keys(nextParams)])
          const changedKeys = []
          allKeys.forEach((key) => {
            if (!isParamValueEqual(previousParams[key], nextParams[key])) {
              changedKeys.push(key)
            }
          })
          return changedKeys
        }
        const toParamsEvent = (previousParams, params, changedKeys, isInitial) => ({
          params,
          previousParams,
          changedKeys,
          revision: paramsState.revision,
          instanceId,
          isInitial,
        })
        const notifyRuntimeParamsListener = (eventPayload) => {
          const listener = runtimeFunctions.get(runtimeParamsListenerName)
          if (!listener) return
          try {
            listener.fn(eventPayload)
          } catch (error) {
            console.warn('[asset-runtime] params listener failed', error)
          }
        }
        const applyParams = (values, revision) => {
          const previousParamsSnapshot = cloneParams()
          paramsState.values = values && typeof values === 'object' ? { ...values } : {}
          paramsState.revision = typeof revision === 'number' ? revision : paramsState.revision + 1
          const params = cloneParams()
          const changedKeys = getChangedKeys(previousParamsSnapshot, params)
          const isInitial = !hasReceivedHostUpdate
          hasReceivedHostUpdate = true
          const eventPayload = toParamsEvent(
            isInitial ? null : previousParamsSnapshot,
            params,
            changedKeys,
            isInitial
          )
          try {
            log('Params update received: ' + JSON.stringify(params))
          } catch (error) {
            log('Params update received')
          }
          notifyRuntimeParamsListener(eventPayload)
        }

        const registerRuntimeFunction = (name, fn, options) => {
          const normalizedName = normalizeName(name)
          if (!normalizedName) {
            throw new Error('window.trellis.runtime.register requires a non-empty function name.')
          }
          if (normalizedName === runtimeParamsListenerName) {
            throw new Error(
              'window.trellis.runtime.register: use registerParamsListener for onParamsChanged listener.'
            )
          }
          if (typeof fn !== 'function') {
            throw new Error('window.trellis.runtime.register requires a function.')
          }
          const entryOptions = options && typeof options === 'object' ? options : {}
          if (runtimeFunctions.has(normalizedName) && !entryOptions.overwrite) {
            throw new Error('window.trellis.runtime.register: function "' + normalizedName + '" already exists.')
          }
          const entry = {
            fn,
            params: normalizeParamsList(entryOptions.params),
            description: typeof entryOptions.description === 'string' ? entryOptions.description : '',
          }
          runtimeFunctions.set(normalizedName, entry)
          return toRuntimeRegistrationInfo(normalizedName, entry)
        }
        const unregisterRuntimeFunction = (name) => {
          const normalizedName = normalizeName(name)
          if (!normalizedName) return false
          if (normalizedName === runtimeParamsListenerName) {
            return runtimeFunctions.delete(runtimeParamsListenerName)
          }
          return runtimeFunctions.delete(normalizedName)
        }
        const invokeRuntimeFunction = (name, ...args) => {
          const normalizedName = normalizeName(name)
          const entry = runtimeFunctions.get(normalizedName)
          if (!entry) {
            throw new Error('window.trellis.runtime.invoke: unknown function "' + normalizedName + '".')
          }
          return entry.fn(...args)
        }
        const listRuntimeFunctions = () =>
          Array.from(runtimeFunctions.entries()).map(([name, entry]) => toRuntimeRegistrationInfo(name, entry))
        const registerParamsListener = (listener, options) => {
          if (typeof listener !== 'function') {
            throw new Error('window.trellis.runtime.registerParamsListener requires a function.')
          }
          const entryOptions = options && typeof options === 'object' ? options : {}
          const entry = {
            fn: listener,
            params: ['event'],
            description:
              typeof entryOptions.description === 'string' && entryOptions.description.trim().length > 0
                ? entryOptions.description
                : 'Called when params are updated by the host.',
          }
          runtimeFunctions.set(runtimeParamsListenerName, entry)
          return toRuntimeRegistrationInfo(runtimeParamsListenerName, entry)
        }
        const clearParamsListener = () => runtimeFunctions.delete(runtimeParamsListenerName)

        const assets = ${serializedAssets}
        const cache = new Map(assets.map((asset) => [String(asset.id).toLowerCase(), asset]))
        const normalizeId = (value) => String(value || '').trim().toLowerCase()
        const stripPrefix = (value) => (String(value || '').trim().startsWith('asset://')
          ? String(value || '').trim().slice('asset://'.length)
          : String(value || '').trim())
        const resolve = (ref) => {
          const id = normalizeId(stripPrefix(ref))
          const asset = cache.get(id)
          if (!asset) {
            throw new Error('Asset not preloaded: ' + ref)
          }
          return asset
        }
        const emit = (payload, targetWindow = window.parent) => {
          if (!targetWindow || typeof targetWindow.postMessage !== 'function') return
          targetWindow.postMessage(payload, '*')
        }
        const postMessage = (targetWindow, payload) => {
          if (!targetWindow || typeof targetWindow.postMessage !== 'function') return
          targetWindow.postMessage(payload, '*')
        }
        const log = (message) => {
          const normalizedMessage = message === null || message === undefined ? '' : String(message)
          emit(
            {
              source: 'asset-renderer',
              type: 'log',
              timestamp: new Date().toISOString(),
              message: normalizedMessage,
            },
            window.parent
          )
          if (!window.parent || window.parent === window) {
            console.log(normalizedMessage)
          }
          return normalizedMessage
        }
        const uiState = { mode: 'hidden', message: '' }
        let uiRoot = null
        let uiSpinner = null
        let uiMessage = null
        const uiStyleId = 'trellis-runtime-ui-style'
        const ensureUiStyle = () => {
          if (document.getElementById(uiStyleId)) return
          const style = document.createElement('style')
          style.id = uiStyleId
          style.textContent =
            '@keyframes trellis-runtime-circular-rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }' +
            '@keyframes trellis-runtime-circular-dash {' +
            '  0% { stroke-dasharray: 1px, 200px; stroke-dashoffset: 0; }' +
            '  50% { stroke-dasharray: 100px, 200px; stroke-dashoffset: -15px; }' +
            '  100% { stroke-dasharray: 100px, 200px; stroke-dashoffset: -125px; }' +
            '}' +
            '@media (prefers-reduced-motion: reduce) {' +
            '  .trellis-runtime-spinner {' +
            '    animation: none !important;' +
            '  }' +
            '  .trellis-runtime-spinner-indicator {' +
            '    animation: none !important;' +
            '    stroke-dasharray: 80px, 200px !important;' +
            '    stroke-dashoffset: 0 !important;' +
            '  }' +
            '}'
          ;(document.head || document.documentElement)?.appendChild(style)
        }
        const ensureUiRoot = () => {
          if (uiRoot && uiRoot.isConnected) return uiRoot
          const body = document.body
          if (!body) return null
          ensureUiStyle()
          const root = document.createElement('div')
          root.setAttribute('data-trellis-ui', 'status')
          root.style.position = 'fixed'
          root.style.inset = '0'
          root.style.display = 'none'
          root.style.alignItems = 'center'
          root.style.justifyContent = 'center'
          root.style.flexDirection = 'column'
          root.style.gap = '12px'
          root.style.padding = '16px'
          root.style.background = '#ffffff'
          root.style.color = uiTextColor
          root.style.textAlign = 'center'
          root.style.fontFamily = "Be Vietnam Pro, Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
          root.style.fontSize = '13px'
          root.style.zIndex = '2147483647'
          root.style.pointerEvents = 'auto'

          const spinner = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
          spinner.setAttribute('class', 'trellis-runtime-spinner')
          spinner.setAttribute('viewBox', '22 22 44 44')
          spinner.setAttribute('aria-hidden', 'true')
          spinner.style.width = '28px'
          spinner.style.height = '28px'
          spinner.style.animation = 'trellis-runtime-circular-rotate 1.4s linear infinite'

          const spinnerTrack = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
          spinnerTrack.setAttribute('cx', '44')
          spinnerTrack.setAttribute('cy', '44')
          spinnerTrack.setAttribute('r', '20.2')
          spinnerTrack.setAttribute('fill', 'none')
          spinnerTrack.setAttribute('stroke-width', '3.6')
          spinnerTrack.setAttribute('stroke', uiTrackColor)

          const spinnerIndicator = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
          spinnerIndicator.setAttribute('class', 'trellis-runtime-spinner-indicator')
          spinnerIndicator.setAttribute('cx', '44')
          spinnerIndicator.setAttribute('cy', '44')
          spinnerIndicator.setAttribute('r', '20.2')
          spinnerIndicator.setAttribute('fill', 'none')
          spinnerIndicator.setAttribute('stroke-width', '3.6')
          spinnerIndicator.setAttribute('stroke-linecap', 'round')
          spinnerIndicator.setAttribute('stroke', uiPrimaryColor)
          spinnerIndicator.setAttribute('stroke-dasharray', '80px, 200px')
          spinnerIndicator.setAttribute('stroke-dashoffset', '0')
          spinnerIndicator.style.animation = 'trellis-runtime-circular-dash 1.4s ease-in-out infinite'

          spinner.appendChild(spinnerTrack)
          spinner.appendChild(spinnerIndicator)

          const message = document.createElement('div')
          message.style.maxWidth = '420px'
          message.style.whiteSpace = 'pre-wrap'
          message.style.wordBreak = 'break-word'
          message.style.display = 'none'

          root.appendChild(spinner)
          root.appendChild(message)
          body.appendChild(root)

          uiRoot = root
          uiSpinner = spinner
          uiMessage = message
          return root
        }
        const setUiMode = (mode, message) => {
          uiState.mode = mode === 'error' ? 'error' : mode === 'loading' ? 'loading' : 'hidden'
          uiState.message = message === null || message === undefined ? '' : String(message)
          const root = ensureUiRoot()
          if (!root) return

          if (uiState.mode === 'hidden') {
            root.style.display = 'none'
            return
          }
          root.style.display = 'flex'
          if (uiSpinner) {
            uiSpinner.style.display = uiState.mode === 'loading' ? 'block' : 'none'
          }
          if (uiMessage) {
            const hasMessage = uiState.message.trim().length > 0
            uiMessage.textContent = uiState.message
            uiMessage.style.display = hasMessage ? 'block' : 'none'
            uiMessage.style.color = uiState.mode === 'error' ? uiErrorColor : uiTextColor
          }
        }
        const ui = {
          showLoading: (message = '') => setUiMode('loading', message),
          hideLoading: () => setUiMode('hidden', ''),
          showError: (message = 'Something went wrong.') => setUiMode('error', message),
          clear: () => setUiMode('hidden', ''),
        }
        if (document.readyState === 'loading') {
          document.addEventListener(
            'DOMContentLoaded',
            () => {
              if (uiState.mode !== 'hidden') {
                setUiMode(uiState.mode, uiState.message)
              }
            },
            { once: true }
          )
        }
        const getParams = () => cloneParams()

        const root = (window.trellis = window.trellis || {})
        root.instanceId = instanceId
        root.emit = emit
        root.postMessage = postMessage
        root.log = log
        root.ui = {
          ...(root.ui && typeof root.ui === 'object' ? root.ui : {}),
          ...ui,
        }
        root.assets = { get: resolve }
        root.params = {
          get: getParams,
        }
        root.runtime = {
          register: registerRuntimeFunction,
          unregister: unregisterRuntimeFunction,
          invoke: invokeRuntimeFunction,
          list: listRuntimeFunctions,
          registerParamsListener,
          clearParamsListener,
        }

        window.addEventListener('message', (event) => {
          const data = event && event.data
          if (!data || data.source !== 'asset-params') return
          if (data.type === 'update' && data.target === instanceId) {
            applyParams(data.values || {}, data.revision)
          }
        })

        emit({ source: 'asset-params', type: 'ready', instanceId }, window.parent)

        const attachEngine = (key, getter) => {
          if (root[key]) return
          const value = getter()
          if (value) {
            root[key] = value
            return
          }
          let attempts = 0
          const interval = setInterval(() => {
            const next = getter()
            if (next) {
              root[key] = next
              clearInterval(interval)
              return
            }
            attempts += 1
            if (attempts >= 60) clearInterval(interval)
          }, 50)
        }

        attachEngine('phaser', () => window.Phaser)
        attachEngine('d3', () => window.d3)
      })()
    </script>
  `
}

const injectTrellisRuntime = (html: string, assets: AssetHandle[], instanceId: string, uiPalette: TrellisUiPalette) => {
  const runtime = buildTrellisRuntimeScript(assets, instanceId, uiPalette)
  if (html.includes('</head>')) {
    return html.replace('</head>', `${runtime}</head>`)
  }
  if (html.includes('</body>')) {
    return html.replace('</body>', `${runtime}</body>`)
  }
  return `${runtime}${html}`
}

const resolveAssetTypeDefinition = (label?: string) => {
  if (!label) return undefined
  return findAssetTypeByLabel(label)
}

const resolveMediaMetaFromAssetType = (assetType?: AssetTypeDefinition) => {
  if (!assetType?.mimeType || !assetType?.mediaType) return undefined
  return { mimeType: assetType.mimeType, mediaType: assetType.mediaType }
}

const normalizeFieldName = (value: string) => value.trim().toLowerCase()

const isUuid = (value: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)

const resolveMediaType = (mimeType?: string): AssetHandle['mediaType'] => {
  if (!mimeType) return 'image'
  if (mimeType.startsWith('audio/')) return 'audio'
  if (mimeType.startsWith('video/')) return 'video'
  if (mimeType.startsWith('text/') || mimeType === 'application/json') return 'text'
  return 'image'
}

const defaultMimeTypeForMedia = (mediaType: AssetHandle['mediaType']) => {
  switch (mediaType) {
    case 'audio':
      return 'audio/mpeg'
    case 'video':
      return 'video/mp4'
    case 'text':
      return 'text/plain'
    default:
      return 'image/png'
  }
}

const isTextMimeType = (mimeType: string) => mimeType.startsWith('text/') || TEXT_MIME_TYPES.has(mimeType)

const base64ToBytes = (value: string) => {
  const cleaned = value.replace(/\s+/g, '')
  const binary = window.atob(cleaned)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

const decodeText = (bytes: Uint8Array) => {
  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder('utf-8').decode(bytes)
  }
  let result = ''
  for (let i = 0; i < bytes.length; i += 1) {
    result += String.fromCharCode(bytes[i])
  }
  return result
}

const encodeText = (value: string) => {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value)
  }
  const bytes = new Uint8Array(value.length)
  for (let i = 0; i < value.length; i += 1) {
    bytes[i] = value.charCodeAt(i)
  }
  return bytes
}

const fetchJson = async (url: string, auth: NonNullable<RenderData['auth']>) => {
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${auth.accessToken}`,
      Accept: 'application/json',
    },
  })
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} ${response.statusText}`)
  }
  return response.json() as Promise<RawEntity>
}

const getEntity = async (id: string, auth: NonNullable<RenderData['auth']>, cache: Map<string, Promise<RawEntity>>) => {
  const key = normalizeAssetId(id)
  const cached = cache.get(key)
  if (cached) return cached
  const promise = fetchJson(
    `${auth.entityApiUrl}/organizations/${auth.organizationId}/entities/${encodeURIComponent(id)}`,
    auth
  ).catch((error) => {
    cache.delete(key)
    throw error
  })
  cache.set(key, promise)
  return promise
}

const getEntityType = async (
  id: string,
  auth: NonNullable<RenderData['auth']>,
  cache: Map<string, Promise<RawEntity>>
) => {
  const key = normalizeAssetId(id)
  const cached = cache.get(key)
  if (cached) return cached
  const promise = fetchJson(
    `${auth.entityApiUrl}/organizations/${auth.organizationId}/types/${encodeURIComponent(id)}`,
    auth
  ).catch((error) => {
    cache.delete(key)
    throw error
  })
  cache.set(key, promise)
  return promise
}

const unwrapEntity = (entity: RawEntity) =>
  entity && typeof entity === 'object' && 'entity' in entity ? entity.entity : entity

const extractId = (value: unknown) => {
  if (typeof value === 'string') return value
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    if (typeof record.value === 'string') return record.value
    if (typeof record.id === 'string') return record.id
  }
  return undefined
}

const getEntityTypeId = (entity: RawEntity): string | undefined => {
  const direct =
    extractId(entity.typeId) ||
    extractId(entity.type_id) ||
    extractId(entity.entityTypeId) ||
    extractId(entity.entity_type_id)
  if (direct) return direct
  const typeCandidate = entity.type
  if (!typeCandidate) return undefined
  if (typeof typeCandidate === 'object') {
    return extractId(typeCandidate) || extractId((typeCandidate as RawEntity).id)
  }
  return extractId(typeCandidate)
}

const findFieldByName = (entityType: RawEntity, fieldName: string) => {
  const fields = Array.isArray(entityType.fields) ? entityType.fields : []
  const normalized = normalizeFieldName(fieldName)
  return fields.find((field) => normalizeFieldName(String((field as RawEntity).name || '')) === normalized) as
    | RawEntity
    | undefined
}

const readFieldValue = (entity: RawEntity, fieldIdValue: unknown) => {
  if (!fieldIdValue) return ''
  const fieldId = extractId(fieldIdValue) ?? String(fieldIdValue)
  const values = entity.valuesByFieldId
  if (!values || typeof values !== 'object') return ''
  const raw = (values as Record<string, unknown>)[fieldId]
  if (raw === null || raw === undefined) return ''
  if (typeof raw === 'string' || typeof raw === 'number') return String(raw)
  if (typeof raw === 'object' && raw && 'value' in raw) {
    const value = (raw as RawEntity).value
    return value === null || value === undefined ? '' : String(value)
  }
  return ''
}

const getEntityLabel = (entity: RawEntity) => {
  const data = unwrapEntity(entity) as RawEntity
  const candidates = [data.primaryLabelText, data.name, data.label, data.title]
  const match = candidates.find((value) => typeof value === 'string' && value.trim().length > 0)
  return match ? String(match).trim() : ''
}

const getEntityId = (entity: RawEntity) =>
  extractId(entity.id) || extractId(entity.entityId) || extractId(entity.entity_id)

const toRenderDataForCollector = (
  assetEntity: RawEntity,
  assetEntityType: RawEntity,
  typeId: string,
  typeLabel: string
): RenderData => {
  const typeFields = Array.isArray(assetEntityType.fields) ? assetEntityType.fields : []
  const fields = typeFields
    .map((field) => {
      const record = field as RawEntity
      const id = extractId(record.id)
      if (!id) return null
      return {
        id,
        name: typeof record.name === 'string' ? record.name : '',
        type: typeof record.type === 'string' ? record.type : '',
        value: readFieldValue(assetEntity, id),
        required: Boolean(record.required),
      }
    })
    .filter((field): field is NonNullable<typeof field> => Boolean(field))

  return {
    entity: {
      id: getEntityId(assetEntity),
      typeId,
      typeName: typeLabel || getEntityLabel(assetEntityType) || typeId,
      fields,
    },
  }
}

const parseDatasetPayloadForHandle = (rawPayload: string): AssetHandle['datasetPayload'] => {
  if (!rawPayload || !rawPayload.trim()) return undefined
  try {
    const parsed = YAML.parse(rawPayload) as { query?: unknown; params?: unknown } | null
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined
    const query = parsed.query ? String(parsed.query).trim() : ''
    if (!query) return undefined
    return { query, params: parsed.params }
  } catch {
    return undefined
  }
}

const resolvePreloadedAsset = async (
  assetId: string,
  auth: NonNullable<RenderData['auth']>,
  entityCache: Map<string, Promise<RawEntity>>,
  typeCache: Map<string, Promise<RawEntity>>
): Promise<ResolvedPreloadedAsset> => {
  const rawEntity = await getEntity(assetId, auth, entityCache)
  const assetEntity = unwrapEntity(rawEntity) as RawEntity
  if (!assetEntity) throw new Error(`Asset ${assetId} not found`)

  const typeId = getEntityTypeId(assetEntity)
  if (!typeId) throw new Error(`Asset ${assetId} is missing type information`)

  const assetType = await getEntityType(typeId, auth, typeCache)
  if (!assetType) throw new Error(`Asset type ${typeId} not found for ${assetId}`)

  const payloadField = findFieldByName(assetType, 'Payload')
  const thumbnailField = findFieldByName(assetType, 'Thumbnail')
  const mimeField = findFieldByName(assetType, 'Mime Type') || findFieldByName(assetType, 'MIME Type')
  const typeField = findFieldByName(assetType, 'Type')

  const payload = payloadField ? readFieldValue(assetEntity, payloadField.id) : ''
  const thumbnail = thumbnailField ? readFieldValue(assetEntity, thumbnailField.id) : ''
  const explicitMime = mimeField ? readFieldValue(assetEntity, mimeField.id) : ''
  let typeLabel = typeField ? readFieldValue(assetEntity, typeField.id) : ''

  if (typeLabel && isUuid(typeLabel)) {
    const typeEntity = await getEntity(typeLabel, auth, entityCache)
    typeLabel = getEntityLabel(typeEntity) || typeLabel
  }

  const normalizedTypeLabel = typeLabel || getEntityLabel(assetType)
  const assetTypeDefinition = resolveAssetTypeDefinition(normalizedTypeLabel)
  const mediaMeta = resolveMediaMetaFromAssetType(assetTypeDefinition)
  const sanitizedExplicitMime = explicitMime && explicitMime.includes('/') ? explicitMime.trim() : ''
  const payloadFormat = assetTypeDefinition?.payloadFormat
  const payloadLooksTextual = payloadFormat === 'text' || resolveMediaType(sanitizedExplicitMime) === 'text'

  let mediaType = mediaMeta?.mediaType ?? (payloadLooksTextual ? 'text' : resolveMediaType(sanitizedExplicitMime))
  let resolvedMime = sanitizedExplicitMime || mediaMeta?.mimeType || defaultMimeTypeForMedia(mediaType)
  const rawPayload = mediaType === 'image' ? payload || thumbnail : payload
  if (!rawPayload) throw new Error(`Asset ${assetId} has no payload`)

  let bytes: Uint8Array
  let text: string | undefined
  if (payloadLooksTextual || mediaType === 'text') {
    mediaType = 'text'
    resolvedMime = sanitizedExplicitMime || mediaMeta?.mimeType || defaultMimeTypeForMedia('text')
    bytes = encodeText(rawPayload)
    text = rawPayload
  } else {
    try {
      bytes = base64ToBytes(rawPayload)
      text = isTextMimeType(resolvedMime) ? decodeText(bytes) : undefined
    } catch {
      mediaType = 'text'
      resolvedMime = defaultMimeTypeForMedia('text')
      bytes = encodeText(rawPayload)
      text = rawPayload
    }
  }

  const blob = new Blob([bytes], { type: resolvedMime })
  const url = URL.createObjectURL(blob)
  const datasetPayload = typeof text === 'string' ? parseDatasetPayloadForHandle(text) : undefined

  return {
    handle: {
      id: normalizeAssetId(assetId),
      mimeType: resolvedMime,
      mediaType,
      url,
      text,
      datasetPayload,
    },
    content: payloadLooksTextual || mediaType === 'text' ? rawPayload : text ?? '',
    renderData: toRenderDataForCollector(assetEntity, assetType, typeId, normalizedTypeLabel),
    collectAssetReferences: assetTypeDefinition?.collectAssetReferences,
  }
}

const AssetRenderer = ({
  content,
  assetType,
  renderData,
  previewWidth,
  previewHeight,
  reloadToken,
  instanceId,
  layout = 'fixed',
  showLog = true,
  forwardLogs = false,
  previewSx,
}: AssetRendererProps) => {
  const theme = useTheme()
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const logRef = useRef<HTMLDivElement | null>(null)
  const [logLines, setLogLines] = useState<RenderLogLine[]>([])
  const [docKey, setDocKey] = useState(0)
  const lastFocusedRef = useRef<HTMLElement | null>(null)
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null)
  const [portalCache, setPortalCache] = useState<EmotionCache | null>(null)
  const instanceIdRef = useRef(instanceId ?? createInstanceId())
  const isFillLayout = layout === 'fill'
  const renderWidth = previewWidth ?? RENDER_WIDTH
  const renderHeight = previewHeight ?? RENDER_HEIGHT
  const usesReactRenderer = Boolean(assetType?.renderComponent)
  const previewBoxSx: SxProps<Theme> = useMemo(() => {
    const base: SxProps<Theme> = {
      width: '100%',
      height: isFillLayout ? '100%' : renderHeight,
      overflow: 'hidden',
      backgroundColor: usesReactRenderer ? 'transparent' : '#fff',
      flexShrink: isFillLayout ? 1 : 0,
      flex: isFillLayout ? 1 : undefined,
      minHeight: 0,
    }
    if (!previewSx) return base
    if (Array.isArray(previewSx)) return [base, ...previewSx]
    return [base, previewSx]
  }, [isFillLayout, previewSx, renderHeight])
  const runtimeBuildHtml = assetType?.buildHtml
  const runtimeCollectAssetReferences = assetType?.collectAssetReferences
  const trellisUiPalette = useMemo<TrellisUiPalette>(
    () => ({
      primary: theme.palette.primary.main,
      track: alpha(theme.palette.primary.main, 0.24),
      text: theme.palette.text.secondary,
      error: theme.palette.error.main,
    }),
    [theme.palette.error.main, theme.palette.primary.main, theme.palette.text.secondary]
  )
  const renderContent = useMemo(
    () => (content !== undefined && content !== null && content.trim().length > 0 ? content : ''),
    [content]
  )
  const [assetStatus, setAssetStatus] = useState<AssetStatus>('loading')
  const [assetError, setAssetError] = useState<string | null>(null)
  const [assetReferences, setAssetReferences] = useState<string[]>([])
  const [assetHandles, setAssetHandles] = useState<AssetHandle[]>([])
  const assetCacheRef = useRef<Map<string, AssetHandle>>(new Map())
  const entityCacheRef = useRef<Map<string, Promise<RawEntity>>>(new Map())
  const typeCacheRef = useRef<Map<string, Promise<RawEntity>>>(new Map())
  const loadTokenRef = useRef(0)

  const pruneAssetCache = useCallback((requiredSet: Set<string>) => {
    const cache = assetCacheRef.current
    Array.from(cache.entries()).forEach(([id, asset]) => {
      if (!requiredSet.has(id)) {
        URL.revokeObjectURL(asset.url)
        cache.delete(id)
      }
    })
  }, [])

  useEffect(() => {
    return () => {
      assetCacheRef.current.forEach((asset) => {
        URL.revokeObjectURL(asset.url)
      })
      assetCacheRef.current.clear()
    }
  }, [])

  useEffect(() => {
    const token = loadTokenRef.current + 1
    loadTokenRef.current = token
    setAssetStatus('loading')
    setAssetError(null)

    const collector = runtimeCollectAssetReferences ?? collectAssetReferencesFromRenderData
    const resolvedAssetCache = new Map<string, Promise<ResolvedPreloadedAsset>>()

    const getResolvedAsset = (id: string) => {
      const normalizedId = normalizeAssetId(id)
      const cached = resolvedAssetCache.get(normalizedId)
      if (cached) return cached

      const auth = renderData?.auth
      if (!auth?.accessToken || !auth.organizationId || !auth.entityApiUrl) {
        throw new Error('Missing auth context for asset preloading.')
      }

      const promise = resolvePreloadedAsset(normalizedId, auth, entityCacheRef.current, typeCacheRef.current).catch(
        (error) => {
          resolvedAssetCache.delete(normalizedId)
          throw error
        }
      )
      resolvedAssetCache.set(normalizedId, promise)
      return promise
    }

    const loadAssets = async () => {
      const resolvedReferences = await collectAssetReferencesRecursive({
        content: renderContent,
        renderData,
        collectAssetReferences: collector,
        resolveAsset: async (id) => {
          const resolvedAsset = await getResolvedAsset(id)
          return {
            content: resolvedAsset.content,
            renderData: resolvedAsset.renderData,
            collectAssetReferences: resolvedAsset.collectAssetReferences,
          }
        },
        maxDepth: Number.MAX_SAFE_INTEGER,
      })

      const requiredIds = Array.from(new Set(resolvedReferences.map((id) => normalizeAssetId(id))))
      const requiredSet = new Set(requiredIds)
      if (loadTokenRef.current !== token) return

      setAssetReferences(requiredIds)

      const cache = assetCacheRef.current
      const missing = requiredIds.filter((id) => !cache.has(id))
      if (missing.length > 0) {
        const loadedAssets = await Promise.all(missing.map(async (id) => (await getResolvedAsset(id)).handle))
        if (loadTokenRef.current !== token) {
          loadedAssets.forEach((asset) => URL.revokeObjectURL(asset.url))
          return
        }
        loadedAssets.forEach((asset) => cache.set(asset.id, asset))
      }

      pruneAssetCache(requiredSet)

      const handles = requiredIds.map((id) => {
        const asset = cache.get(id)
        if (!asset) throw new Error(`Asset ${id} missing after preload`)
        return asset
      })

      if (loadTokenRef.current !== token) return
      setAssetHandles(handles)
      setAssetStatus('ready')
    }

    loadAssets().catch((error) => {
      if (loadTokenRef.current !== token) return
      pruneAssetCache(new Set())
      setAssetReferences([])
      setAssetHandles([])
      setAssetStatus('error')
      setAssetError(error instanceof Error ? error.message : String(error))
    })
  }, [pruneAssetCache, renderContent, renderData, runtimeCollectAssetReferences])

  const html = useMemo(() => {
    if (assetStatus !== 'ready') {
      if (assetStatus === 'error') {
        return buildStatusHtml('Asset preload failed.', assetError ?? 'Unknown error.')
      }
      const loadingMessage =
        assetReferences.length > 0
          ? `Preloading assets... Waiting for ${assetReferences.length} asset(s).`
          : 'Preloading assets...'
      return buildLoadingHtml(loadingMessage)
    }
    const builder = runtimeBuildHtml ?? (() => buildMissingBuildHtml(assetType))
    let baseHtml = ''
    try {
      const builtHtml = builder(renderContent, { data: renderData })
      if (!builtHtml || typeof builtHtml !== 'string') {
        baseHtml = buildStatusHtml(
          'Asset runtime contract violation.',
          'buildHtml(content, options) must return a non-empty HTML string.'
        )
      } else {
        baseHtml = builtHtml
      }
    } catch (error) {
      baseHtml = buildStatusHtml(
        'Asset runtime buildHtml failed.',
        error instanceof Error ? error.message : String(error)
      )
    }
    return injectTrellisRuntime(baseHtml, assetHandles, instanceIdRef.current, trellisUiPalette)
  }, [
    assetType,
    assetError,
    assetHandles,
    assetReferences.length,
    assetStatus,
    renderContent,
    renderData,
    runtimeBuildHtml,
    trellisUiPalette,
  ])

  useEffect(() => {
    // Capture focus before forcing iframe remount
    lastFocusedRef.current = document.activeElement as HTMLElement | null
    setDocKey((prev) => prev + 1)
  }, [html, reloadToken])

  useEffect(() => {
    if (!usesReactRenderer) {
      setPortalRoot(null)
      setPortalCache(null)
      return
    }
    setPortalRoot(null)
    setPortalCache(null)
  }, [docKey, usesReactRenderer])

  const appendLog = useCallback((message: string) => {
    const split = message.replace(/\r/g, '').split('\n')
    if (split.length > 1 && split[split.length - 1] === '') split.pop()
    if (split.length === 0) return

    setLogLines((previous) => {
      const next = [...previous]
      split.forEach((line) => {
        const sqlMatch = line.match(SQL_CONTINUATION_LINE_PATTERN) ?? line.match(RAW_SQL_CONTINUATION_LINE_PATTERN)
        if (sqlMatch) {
          const cleaned = (sqlMatch[1] ?? '').trimEnd()
          if (next.length === 0) {
            next.push({ text: '[...]', collapsedLines: [cleaned] })
            return
          }
          const lastIndex = next.length - 1
          const last = next[lastIndex]
          next[lastIndex] = {
            ...last,
            collapsedLines: [...(last.collapsedLines ?? []), cleaned],
          }
          return
        }

        next.push({ text: line })
      })
      return next
    })
  }, [])

  const appendLogWithTimestamp = useCallback(
    (message: string) => {
      const timestamp = new Date().toISOString()
      appendLog(`[${timestamp}] ${message}`)
    },
    [appendLog]
  )

  useEffect(() => {
    setLogLines([{ text: 'Render log initialized...' }])
  }, [html])

  useEffect(() => {
    if (!logRef.current) return
    logRef.current.scrollTop = logRef.current.scrollHeight
  }, [logLines])

  useEffect(() => {
    assetParamsBus.ensureListener()
  }, [])

  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe?.contentWindow) return
    const instanceKey = instanceIdRef.current
    assetParamsBus.registerInstance(instanceKey, iframe.contentWindow)
    return () => {
      assetParamsBus.unregisterInstance(instanceKey, iframe.contentWindow)
    }
  }, [docKey])

  useEffect(() => {
    if (instanceId) return
    assetParamsBus.setParamsForInstance(instanceIdRef.current, {})
  }, [docKey, instanceId])

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.source === 'asset-renderer' && event.data?.type === 'log') {
        const line = `[${event.data.timestamp}] ${event.data.message}`
        if (showLog) {
          appendLog(line)
        } else if (!forwardLogs) {
          console.log(line)
        }
        if (forwardLogs && window.parent && window.parent !== window) {
          window.parent.postMessage(event.data, '*')
        }
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [appendLog, forwardLogs, showLog])

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    const iframe = iframeRef.current
    if (!iframe || !previouslyFocused) return

    const handleLoad = () => {
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus()
    }

    iframe.addEventListener('load', handleLoad)
    return () => iframe.removeEventListener('load', handleLoad)
  }, [html])

  useEffect(() => {
    if (!usesReactRenderer) return
    const iframe = iframeRef.current
    if (!iframe) return

    const handleLoad = () => {
      const doc = iframe.contentDocument
      if (!doc) return
      const root = doc.getElementById('root')
      if (!root) {
        appendLogWithTimestamp('Form render failed: iframe root missing.')
        return
      }
      setPortalRoot(root)
      setPortalCache(createCache({ key: 'iframe', container: doc.head }))
    }

    iframe.addEventListener('load', handleLoad)
    handleLoad()
    return () => iframe.removeEventListener('load', handleLoad)
  }, [appendLogWithTimestamp, docKey, usesReactRenderer])

  const portalContent = useMemo(() => {
    const RenderComponent = assetType?.renderComponent
    if (!RenderComponent || !portalRoot || !portalCache) return null
    const props: AssetRenderComponentProps = {
      content: renderContent,
      renderData,
      previewWidth,
      previewHeight,
      reloadToken,
      layout,
      showLog,
      previewSx,
      onLog: appendLogWithTimestamp,
    }
    return createPortal(
      <CacheProvider value={portalCache}>
        <RenderComponent {...props} />
      </CacheProvider>,
      portalRoot
    )
  }, [
    appendLogWithTimestamp,
    assetType?.renderComponent,
    layout,
    portalCache,
    portalRoot,
    previewHeight,
    previewSx,
    previewWidth,
    reloadToken,
    renderContent,
    renderData,
    showLog,
  ])

  return (
    <Stack
      spacing={2}
      sx={{
        width: '100%',
        maxWidth: isFillLayout ? 'none' : renderWidth,
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box sx={previewBoxSx}>
        <iframe
          key={docKey}
          ref={(node) => {
            iframeRef.current = node
            if (node) {
              node.onload = () => {
                // Restore focus to the element that had focus before reload, if still in the document
                const toFocus = lastFocusedRef.current
                if (toFocus && document.contains(toFocus)) {
                  toFocus.focus({ preventScroll: true })
                }
              }
            }
          }}
          title="Asset render"
          srcDoc={html}
          style={{ border: 'none', width: '100%', height: '100%' }}
          sandbox="allow-same-origin allow-scripts"
          tabIndex={-1}
        />
        {portalContent}
      </Box>
      {showLog && (
        <Box
          component="div"
          id="render-log"
          ref={logRef}
          sx={{
            flex: 1,
            minHeight: 0,
            fontFamily: 'JetBrains Mono, Fira Code, monospace',
            fontSize: 12,
            lineHeight: 1.4,
            bgcolor: 'grey.50',
            color: 'text.primary',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            p: 2,
            boxSizing: 'border-box',
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
          }}
        >
          {logLines.map((line, index) => (
            <Box key={`${index}-${line.text}`} component="div">
              <Box component="span">{line.text}</Box>
              {line.collapsedLines && line.collapsedLines.length > 0 && (
                <Tooltip
                  placement="top-start"
                  arrow
                  title={
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        fontFamily: 'JetBrains Mono, Fira Code, monospace',
                        fontSize: 11,
                        lineHeight: 1.45,
                        whiteSpace: 'pre-wrap',
                        maxHeight: '60vh',
                        overflowY: 'auto',
                        pr: 0.5,
                      }}
                    >
                      {line.collapsedLines.join('\n')}
                    </Box>
                  }
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 'min(90vw, 1200px)',
                        p: 1,
                      },
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      ml: 0.5,
                      px: 0.5,
                      borderRadius: 0.5,
                      bgcolor: 'grey.200',
                      color: 'text.secondary',
                      cursor: 'help',
                      userSelect: 'none',
                      '&:hover': {
                        bgcolor: 'grey.300',
                        color: 'text.primary',
                      },
                    }}
                  >
                    [...]
                  </Box>
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Stack>
  )
}

export default AssetRenderer
